import { z } from 'zod';

/**
 * Een account laten verwijderen (DEL-1, ADR-0098).
 *
 * Twee wegen naar hetzelfde verzoek: aangemeld in de app (met wachtwoord of pincode als
 * bevestiging), of zonder aanmelden via de publieke pagina `/account-verwijderen` (met een link in
 * de mail als bevestiging). De app- en de Play-winkel eisen allebei dat het kan; de tweede weg is
 * er voor wie de app al verwijderde.
 *
 * Het verzoek schakelt meteen uit; de gegevens verdwijnen pas na een bedenktijd van dertig dagen.
 */

/** Alleen dit bedrijf, of alles: de identiteit en elk gekoppeld account (ADR-0080). */
export const accountDeletionScopeSchema = z.enum(['tenant', 'all']);
export type AccountDeletionScope = z.infer<typeof accountDeletionScopeSchema>;

/** Wat het scherm "Account verwijderen" vooraf moet weten. */
export const accountDeletionOverviewSchema = z.object({
  /** Waarmee de persoon zich bevestigt: zijn wachtwoord, of (zonder wachtwoord) zijn pincode. */
  verification: z.enum(['password', 'pin']),
  /** Het bedrijf van deze sessie en de gekoppelde bedrijven; bij één bedrijf is er geen keuze. */
  organisations: z.array(z.object({ tenantId: z.guid(), name: z.string(), current: z.boolean() })),
  /** Krijgt de persoon een mail met een link om te annuleren? Niet zonder e-mailadres. */
  hasEmail: z.boolean(),
  /** De bedenktijd in dagen. */
  graceDays: z.number().int(),
});
export type AccountDeletionOverview = z.infer<typeof accountDeletionOverviewSchema>;

export const requestAccountDeletionSchema = z.object({
  scope: accountDeletionScopeSchema,
  /** Het wachtwoord, of de pincode voor wie zonder wachtwoord aanmeldt. */
  secret: z.string().min(1).max(200),
});
export type RequestAccountDeletion = z.infer<typeof requestAccountDeletionSchema>;

/** Een bedrijf waar het verzoek niet kon, met de reden in mensentaal. */
export const accountDeletionRefusalSchema = z.object({
  organisation: z.string(),
  reason: z.enum(['last_admin', 'platform_admin']),
});
export type AccountDeletionRefusal = z.infer<typeof accountDeletionRefusalSchema>;

export const accountDeletionResultSchema = z.object({
  /** Hoeveel accounts er nu uitgeschakeld zijn en op wissen wachten. */
  accounts: z.number().int(),
  /** Vanaf wanneer de gegevens gewist worden. Leeg als er niets geregistreerd werd. */
  eraseAfter: z.string().datetime().nullable(),
  refused: z.array(accountDeletionRefusalSchema),
});
export type AccountDeletionResult = z.infer<typeof accountDeletionResultSchema>;

/** Publiek: een link aanvragen. Het antwoord is altijd `202`, of het adres bestaat of niet. */
export const publicAccountDeletionRequestSchema = z.object({
  email: z.string().email().max(320),
});
export type PublicAccountDeletionRequest = z.infer<typeof publicAccountDeletionRequestSchema>;

/** Publiek: de link uit de mail, om te bevestigen of om te annuleren. */
export const accountDeletionTokenSchema = z.object({
  token: z.string().min(20).max(200),
});
export type AccountDeletionToken = z.infer<typeof accountDeletionTokenSchema>;

export const accountDeletionCancelledSchema = z.object({
  /** Hoeveel accounts er weer aan staan. */
  accounts: z.number().int(),
});
export type AccountDeletionCancelled = z.infer<typeof accountDeletionCancelledSchema>;

/** Wat een beheerder ziet: wie gevraagd heeft om verwijderd te worden, en wanneer het gebeurt. */
export const pendingAccountDeletionSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  origin: z.enum(['app', 'web']),
  requestedAt: z.string().datetime(),
  eraseAfter: z.string().datetime(),
});
export type PendingAccountDeletion = z.infer<typeof pendingAccountDeletionSchema>;

export const pendingAccountDeletionListSchema = z.object({
  items: z.array(pendingAccountDeletionSchema),
});
export type PendingAccountDeletionList = z.infer<typeof pendingAccountDeletionListSchema>;
